import type { BaselineControlResponse } from "../../../../api/frameworksApi";
import { sourceValues } from "./parameterFormatter";
import {
  collectParamRecords,
  getNestedParamSource,
  getParamId,
  textValues,
} from "./parameterParser";

export type ParameterDisplayRow = {
  paramId: string;
  label?: string;
  catalogValues: string[];
  profileValues: string[];
  componentValues: string[];
  constraints: string[];
  guidelines: string[];
  isOverridden?: boolean;
};

export function getBaselineControlId(control: BaselineControlResponse): string {
  const raw = control as unknown as Record<string, unknown>;
  return String(
    control.controlId ||
    control.control_id ||
    raw.control_id ||
    raw["control-id"] ||
    raw.id ||
    "",
  );
}

export function findBaselineControlForId(
  controls: BaselineControlResponse[],
  controlId: string,
): BaselineControlResponse | undefined {
  const normalizedId = controlId.trim().toLowerCase();
  if (!normalizedId) return undefined;

  return controls.find((control) => {
    const raw = control as unknown as Record<string, unknown>;
    const candidateIds = [
      getBaselineControlId(control),
      control.controlDetails?.control_id,
      String(raw.controlId ?? ""),
      String(raw.control_id ?? ""),
      String(raw["control-id"] ?? ""),
      ...(control.implementedRequirements ?? []).map((ir) => ir.controlId),
    ];
    return candidateIds.some(
      (candidate) => candidate?.trim().toLowerCase() === normalizedId,
    );
  });
}

export function buildParameterRows(
  control: BaselineControlResponse | undefined,
  implementationParams: { paramId: string; values: string }[],
): ParameterDisplayRow[] {
  const componentValueByParam = new Map(
    implementationParams
      .filter((param) => param.paramId?.trim())
      .map((param) => [param.paramId, textValues(param.values.split(","))]),
  );

  const rows = new Map<string, ParameterDisplayRow>();
  const upsert = (row: ParameterDisplayRow) => {
    const existing = rows.get(row.paramId);
    rows.set(row.paramId, {
      paramId: row.paramId,
      label: row.label || existing?.label,
      catalogValues: row.catalogValues.length
        ? row.catalogValues
        : (existing?.catalogValues ?? []),
      profileValues: row.profileValues.length
        ? row.profileValues
        : (existing?.profileValues ?? []),
      componentValues: row.componentValues.length
        ? row.componentValues
        : (existing?.componentValues ??
          componentValueByParam.get(row.paramId) ??
          []),
      constraints: row.constraints.length
        ? row.constraints
        : (existing?.constraints ?? []),
      guidelines: row.guidelines.length
        ? row.guidelines
        : (existing?.guidelines ?? []),
      isOverridden: row.isOverridden ?? existing?.isOverridden,
    });
  };

  const consumedParamRecords = new Set<Record<string, unknown>>();
  const addNestedParameter = (param: Record<string, unknown>) => {
    consumedParamRecords.add(param);
    const paramId = getParamId(param);
    if (!paramId.trim()) return;
    const catalogSource = getNestedParamSource(param, "catalog");
    const profileSource = getNestedParamSource(param, "profile");
    const componentSource = getNestedParamSource(
      param,
      "componentDefinition",
      "component_definition",
      "component",
      "implementation",
    );
    const componentSourceValues = componentSource
      ? textValues(
        componentSource.values ??
        componentSource.value ??
        componentSource.vals ??
        componentSource["set-values"] ??
        componentSource.setValues,
      )
      : [];
    upsert({
      paramId,
      label: String(
        param.label ?? profileSource?.label ?? catalogSource?.label ?? "",
      ),
      catalogValues: sourceValues(
        param,
        "catalog",
        "catalogValues",
        "catalog_values",
        "catalogValue",
        "catalog_value",
      ),
      profileValues: sourceValues(
        param,
        "profile",
        "profileValues",
        "profile_values",
        "profileValue",
        "profile_value",
      ),
      componentValues:
        componentSourceValues.length > 0
          ? componentSourceValues
          : sourceValues(
            param,
            "componentDefinition",
            "component_definition",
            "componentDefinitionValues",
            "component_definition_values",
            "componentDefinitionValue",
            "component_definition_value",
            "componentValues",
            "component_values",
            "implementationValues",
            "implementation_values",
          ).length > 0
            ? sourceValues(
              param,
              "componentDefinition",
              "component_definition",
              "componentDefinitionValues",
              "component_definition_values",
              "componentDefinitionValue",
              "component_definition_value",
              "componentValues",
              "component_values",
              "implementationValues",
              "implementation_values",
            )
            : (componentValueByParam.get(paramId) ?? []),
      constraints: textValues(profileSource?.constraints).length
        ? textValues(profileSource?.constraints)
        : textValues(catalogSource?.constraints),
      guidelines: textValues(profileSource?.guidelines).length
        ? textValues(profileSource?.guidelines)
        : textValues(catalogSource?.guidelines),
      isOverridden: Boolean(param.isOverridden ?? param.is_overridden),
    });
  };

  ((control?.params ?? []) as Record<string, unknown>[]).forEach(
    addNestedParameter,
  );
  collectParamRecords(control).forEach((param) => {
    if (!consumedParamRecords.has(param)) addNestedParameter(param);
  });

  (control?.parameters ?? []).forEach((param) => {
    if (!param.paramId?.trim()) return;
    upsert({
      paramId: param.paramId,
      label: param.label,
      catalogValues: textValues(param.catalogValues),
      profileValues: textValues(param.profileValues),
      componentValues: componentValueByParam.get(param.paramId) ?? [],
      constraints: textValues(param.constraints),
      guidelines: [],
    });
  });

  (
    (control?.controlDetails?.params ?? []) as Record<string, unknown>[]
  ).forEach((param) => {
    const paramId = getParamId(param);
    if (!paramId.trim()) return;
    upsert({
      paramId,
      label: String(param.label ?? ""),
      catalogValues: textValues(param.values),
      profileValues: [],
      componentValues: componentValueByParam.get(paramId) ?? [],
      constraints: [],
      guidelines: textValues(param.guidelines),
    });
  });

  implementationParams.forEach((param) => {
    if (!param.paramId?.trim() || rows.has(param.paramId)) return;
    upsert({
      paramId: param.paramId,
      catalogValues: [],
      profileValues: [],
      componentValues: componentValueByParam.get(param.paramId) ?? [],
      constraints: [],
      guidelines: [],
    });
  });

  return Array.from(rows.values());
}
